// Checkout / plan / subscription API fns — carved verbatim from
// `src/api/scribeAPIV2.ts` (which now re-exports from here) over the E5
// configured-client registry. Request bodies are byte-identical, including
// the hardcoded `vendor: 'stripe'` the backend contract expects.
//
// DOMAIN RULING (D1, ARCFE-972): `changePlan`, `createCheckoutSession`,
// `createAddOnProductCheckoutSession` and `updateSubscriptionWithAddOnProduct`
// were `@domain REVIEW`; `applySubscriptionDiscount` was `@domain workspace`
// on the `/billings/*` route. All five serve exactly one commerce-claimed
// hook each and sit inside the 2.13-ratified subscription boundary, so they
// are ruled `@domain commerce` at carve. The `applySubscriptionDiscount`
// retag is the narrower half of the same P0.4-class ruling that sends the
// billing READS to `commerce/billing` at the IP2 transfer — noted so the
// two are read together, not as independent decisions.

import { getScribeHttpClients } from '@heidi/api/client';
import {
  type CheckoutSessionResponseV2,
  type CreateAddOnCheckoutRequest,
  type CreateCheckoutSessionInputWithoutVendor,
  type CreateCheckoutSessionResponse,
  type Period,
  type ProductView,
  type RecordCancellationIntentRequest,
  type RecordCancellationIntentResponse,
  type RequestSubscriptionUpgradeRequest,
  type RequestSubscriptionUpgradeResponse,
  type StripeFreeTrialDays,
  type SubscriptionTier,
  type components,
} from '../../contracts';
import { type CommerceHttpClient } from '../config';

const http = (): CommerceHttpClient => getScribeHttpClients().scribeHttpV2;

/**
 * The widget-launch transport. Same registry, different instance: widget
 * sessions authenticate with an integration token rather than the main
 * session bearer, which is the entire difference between this and `http()`.
 */
const openApi = (): CommerceHttpClient => getScribeHttpClients().openApiHttp;

/** @domain commerce */
export const changePlan = (input: {
  period: Period;
  tier: SubscriptionTier;
  free_trial_days: StripeFreeTrialDays;
}) => {
  return http().post<{ products: ProductView[] }>(`/payments/change-plan`, {
    vendor: 'stripe',
    period: input.period,
    tier: input.tier,
    free_trial_days: input.free_trial_days,
  });
};

/** @domain commerce */
export const createCheckoutSession = (input: CreateCheckoutSessionInputWithoutVendor) => {
  return http().post<CheckoutSessionResponseV2>(`/payments/checkout`, {
    vendor: 'stripe',
    ...input,
  });
};

/**
 * The OpenAPI twin of `createCheckoutSession`, for widget launches that hold
 * an integration-auth token instead of a session bearer.
 *
 * DOMAIN RULING BY ROUTE: `payments/checkout` on the openApi instance is the
 * same billing resource `createCheckoutSession` posts to on the scribe v2
 * instance, so it is commerce by the test D1 applied to its sibling — one
 * commerce-claimed caller, inside the 2.13-ratified subscription boundary.
 * It was NOT part of the D1 hub sweep because it lived in
 * `src/api/scribeOpenAPI.ts`, which is not one of the `API_HUB` files the
 * sweep reads; it is carved here with the checkout mutation that selects
 * between the two, so the selection itself is package data logic rather than
 * a branch over two injected functions. The old path re-exports it name-intact.
 * @domain commerce
 */
export const createOpenApiCheckoutSession = (
  input: CreateCheckoutSessionInputWithoutVendor
) => {
  return openApi().post<CheckoutSessionResponseV2>(`payments/checkout`, {
    vendor: 'stripe',
    ...input,
  });
};

/** @domain commerce */
export const createAddOnProductCheckoutSession = (
  addOnProductId: string,
  params: CreateAddOnCheckoutRequest
) => {
  return http().post<CreateCheckoutSessionResponse>(
    `/payments/add-on-product/checkout/${addOnProductId}`,
    params
  );
};

/** @domain commerce */
export const updateSubscriptionWithAddOnProduct = ({
  add_on_product_id,
}: {
  add_on_product_id: string;
}) => {
  return http().post<null>(
    `/payments/add-on-product/${add_on_product_id}/update-subscription`
  );
};

/**
 * Apply a configured Stripe coupon for the given discount type to the user's active subscription.
 * @domain commerce
 */
export const applySubscriptionDiscount = (
  body: components['schemas']['ApplySubscriptionDiscountRequest']
) => {
  return http().post<components['schemas']['ApplySubscriptionDiscountResponse']>(
    '/billings/apply-discount',
    body
  );
};

/**
 * Persist cancel reason + return personalised offer routing (GRO-3824).
 * @domain commerce
 */
export const recordCancellationIntent = (body: RecordCancellationIntentRequest) => {
  return http().post<RecordCancellationIntentResponse>(
    '/billings/cancellation-intent',
    body
  );
};

/**
 * POST /api/v2/ml-scribe/teams/{team_id}/request-plan-upgrade — emails each
 * team admin that a non-admin member requested a subscription upgrade.
 *
 * DOMAIN RULING (D2, ARCFE-972): this one was never in `scribeAPIV2.ts` at
 * all — `useMutateRequestTeamPlanUpgrade` called `scribeHttpV2.post` inline,
 * so the D1 hub sweep could not see it. Carving it here as its hook moves is
 * the same ruling by the same test (one commerce-claimed caller, inside the
 * 2.13 boundary), just discovered at the hook rather than at the hub.
 * @domain commerce
 */
export const requestTeamPlanUpgrade = (
  teamId: string,
  body: RequestSubscriptionUpgradeRequest
) => {
  return http().post<RequestSubscriptionUpgradeResponse>(
    `/teams/${teamId}/request-plan-upgrade`,
    body
  );
};
